import { useEffect, useMemo } from "react";
import LBox from "@components/LBox";
import LFlatList from "@components/LFlatList";
import LText from "@components/LText";
import { useAccessTickets } from "@hooks/trpc/useAccesses";
import { theme } from "@theme";
import { RefreshControl } from "react-native-gesture-handler";
import { ActivityIndicator } from "react-native";
import { useLocalSearchParams, router } from "expo-router";
import AccessListItem from "@components/Accesses/components/AccessListItem";
import { sortWallet } from "@libs/wire";

const WalletScreen = () => {
  const ticketsQuery = useAccessTickets();
  const { refetch } = ticketsQuery;
  const { showTicketAccessId } = useLocalSearchParams<{
    showTicketAccessId?: string;
  }>();

  console.log("::WalletScreen - showTicketAccessId", showTicketAccessId);

  // Newest first, as the API orders it: events by their start, merch
  // orders by the day they were placed.
  const data = useMemo(
    () => sortWallet(ticketsQuery.data ?? []),
    [ticketsQuery.data],
  );

  // A link to tickets opens them over the wallet, a seller's as an engine's.
  // Once the wallet is read on this visit: a cached list can predate a
  // purchase outside the app. Without them here (another account, a ticket
  // given back) the link still opens the event. The param is spent as it is
  // read, so the same link opens them again the next time it is tapped.
  useEffect(() => {
    if (!showTicketAccessId) return;
    let cancelled = false;
    refetch().then(({ data }) => {
      if (cancelled) return;
      const selected = data?.find((access) => access.id === showTicketAccessId);
      const held =
        selected?.type === "event" &&
        selected.userTickets?.some(
          (ticket) => ticket.redeemCode !== null || ticket.fromSeller,
        );
      router.setParams({ showTicketAccessId: undefined });
      router.push({
        pathname: held
          ? "/(app)/access/[accessId]/view-tickets"
          : "/(app)/access/[accessId]",
        params: { accessId: showTicketAccessId },
      });
    }).catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [showTicketAccessId, refetch]);

  return (
    <LFlatList
      showsVerticalScrollIndicator={false}
      style={{ flexGrow: 1 }}
      contentContainerStyle={{ flexGrow: 1, rowGap: theme.spacing.s4 }}
      data={data}
      keyExtractor={(item, i) => (item ? item.id : i.toString())}
      ListEmptyComponent={
        ticketsQuery.isLoading ? (
          <ActivityIndicator color={theme.colors.white} />
        ) : (
          <LBox flex={1} alignItems="center" justifyContent="center">
            <LBox width="70%">
              <LText
                variant="inputTitle"
                color="textGrey"
                textAlign="center"
                lineHeight={20}
              >
                You have no items yet. Your tickets and orders will appear here
              </LText>
            </LBox>
          </LBox>
        )
      }
      renderItem={({ item }) => <AccessListItem access={item} wallet />}
      refreshControl={
        <RefreshControl
          refreshing={
            ticketsQuery.isRefetching && ticketsQuery.isFetchedAfterMount
          }
          onRefresh={() => {
            ticketsQuery.refetch().catch(console.log);
          }}
          tintColor={"white"}
        />
      }
    />
  );
};

export default WalletScreen;
